import React, { useState } from 'react';
import { apiService } from '../services';
import { Workout, CreateWorkoutInput, WorkoutType } from '../types/workout';
import WorkoutForm from './WorkoutForm';

interface Event {
  id: string;
  name: string;
  workouts: Workout[];
}

interface WorkoutSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
  onWorkoutsUpdated: () => void;
}

const WorkoutSidePanel: React.FC<WorkoutSidePanelProps> = ({ isOpen, onClose, event, onWorkoutsUpdated }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState<Workout | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAddWorkout = async (workoutData: CreateWorkoutInput) => {
    if (!event) return;

    setLoading(true);
    try {
      const workoutId = `workout-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      await apiService.post(`/events/${event.id}/workouts`, {
        workout: {
          id: workoutId,
          ...workoutData,
          results: [],
        },
      });
      setShowForm(false);
      onWorkoutsUpdated();
    } catch (err: any) {
      throw new Error(err.message || 'Failed to add workout');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkout = async (workoutId: string) => {
    if (!event || !window.confirm('Are you sure you want to delete this workout?')) return;

    setLoading(true);
    try {
      // For now, we'll update the event with filtered workouts
      const updatedWorkouts = event.workouts.filter(w => w.id !== workoutId);
      await apiService.put(`/events/${event.id}`, {
        workouts: updatedWorkouts,
      });
      onWorkoutsUpdated();
    } catch (err: any) {
      alert('Failed to delete workout: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditWorkout = (workout: Workout) => {
    setEditingWorkout(workout);
    setShowForm(true);
  };

  const handleUpdateWorkout = async (workoutData: CreateWorkoutInput) => {
    if (!event || !editingWorkout) return;

    setLoading(true);
    try {
      const updatedWorkouts = event.workouts.map(w =>
        w.id === editingWorkout.id
          ? { ...w, ...workoutData }
          : w
      );
      await apiService.put(`/events/${event.id}`, {
        workouts: updatedWorkouts,
      });
      setShowForm(false);
      setEditingWorkout(null);
      onWorkoutsUpdated();
    } catch (err: any) {
      throw new Error(err.message || 'Failed to update workout');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingWorkout(null);
  };

  const getWorkoutTypeLabel = (type: WorkoutType): string => {
    switch (type) {
      case WorkoutType.FOR_TIME: return 'For Time';
      case WorkoutType.AMRAP: return 'AMRAP';
      case WorkoutType.EMOM: return 'EMOM';
      case WorkoutType.MAX_TIME: return 'Max Time';
      case WorkoutType.MAX_WEIGHT: return 'Max Weight';
      default: return type;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Side Panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Manage Workouts</h2>
            <p className="text-sm text-blue-100">{event?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showForm ? (
            <>
              {/* Add Button */}
              <button
                onClick={() => setShowForm(true)}
                className="w-full mb-6 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-800 transition-all flex items-center justify-center"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add New Workout
              </button>

              {/* Workouts List */}
              {event?.workouts && event.workouts.length > 0 ? (
                <div className="space-y-4">
                  {event.workouts.map((workout) => (
                    <div
                      key={workout.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{workout.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{workout.description}</p>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => handleEditWorkout(workout)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteWorkout(workout.id)}
                            disabled={loading}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center text-gray-700">
                          <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          <span className="font-medium">{getWorkoutTypeLabel(workout.type)}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{workout.timeCap} min cap</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="font-medium">{workout.category}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span>{workout.items.length} exercises</span>
                        </div>
                      </div>

                      {workout.items.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-500 mb-2">Exercises:</p>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {workout.items.map((item, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">No workouts yet</h3>
                  <p className="mt-2 text-gray-600">Add your first workout to get started.</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Form Header */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingWorkout ? 'Edit Workout' : 'Add New Workout'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Fill in the workout details below
                </p>
              </div>

              {/* Form */}
              <WorkoutForm
                workout={editingWorkout || undefined}
                onSubmit={editingWorkout ? handleUpdateWorkout : handleAddWorkout}
                onCancel={handleCancelForm}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default WorkoutSidePanel;
